import React, { useCallback, useEffect, useState } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { socialApi } from '../../support/supportApi';
import { START_CONVERSATION_LABEL } from './messagingLabels';
import { normalizeMessagingUser, formatMessagingHandle } from './messagingUserUtils';
import MessagingPeopleSkeleton from './MessagingPeopleSkeleton';
import { applyProfileAvatarImgFallback } from '../profilePictureUrl';
import './Messaging.css';

function UserRow({ user, onSelect }) {
  const normalized = normalizeMessagingUser(user);
  if (!normalized) return null;

  return (
    <li>
      <button
        type="button"
        className="messaging-new-message__user noOutline-text"
        onClick={() => onSelect?.(normalized)}
      >
        <img
          src={normalized.profilePictureUrl}
          alt=""
          className="messaging-new-message__avatar"
          onError={(e) => {
            applyProfileAvatarImgFallback(e.currentTarget, {
              userId: normalized.userId,
              name: normalized.displayName,
            });
          }}
        />
        <span className="messaging-new-message__name">{normalized.displayName}</span>
        {normalized.handle ? (
          <span className="messaging-new-message__handle">{formatMessagingHandle(normalized.handle)}</span>
        ) : null}
      </button>
    </li>
  );
}

/**
 * Pick someone to message — search by handle or browse following.
 */
export default function NewMessagePicker({ viewerUserId, onSelectUser, onClose, embedded = false }) {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  const loadFollowing = useCallback(async () => {
    if (!viewerUserId) return;
    setLoading(true);
    try {
      const data = await socialApi.following({
        userId: viewerUserId,
        profileMode: 'personal',
        forMessaging: true,
        skip: 0,
        limit: 60,
      });
      setFollowing(data?.users || []);
    } catch {
      setFollowing([]);
    } finally {
      setLoading(false);
    }
  }, [viewerUserId]);

  useEffect(() => {
    loadFollowing();
  }, [loadFollowing]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await socialApi.profileSearch({ q, limit: 20, forMessaging: true });
        const profiles = (data?.profiles || [])
          .map((p) => normalizeMessagingUser(p))
          .filter(Boolean);
        setSearchResults(profiles);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, viewerUserId]);

  const showSearch = query.trim().length >= 2;
  const listUsers = showSearch ? searchResults : following;
  const showSkeleton =
    (!showSearch && loading) || (showSearch && searching && !searchResults.length);
  const emptyLabel = showSearch
    ? searching
      ? null
      : 'No profiles found.'
    : loading
      ? null
      : 'Follow someone to start messaging them.';

  return (
    <div className={`messaging-new-message${embedded ? ' messaging-new-message--embedded' : ''}`}>
      <div className="messaging-new-message__header">
        <h2>{START_CONVERSATION_LABEL}</h2>
        {onClose ? (
          <button type="button" className="messaging-new-message__close noOutline-text" onClick={onClose} aria-label="Close">
            <FiX aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="messaging-new-message__search">
        <FiSearch aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by @handle"
          aria-label="Search people"
          autoFocus
        />
      </div>

      <p className="messaging-new-message__hint">
        {showSearch ? 'Search results' : 'People you follow'}
      </p>

      {showSkeleton ? (
        <MessagingPeopleSkeleton
          rows={6}
          listClassName="messaging-new-message__list messaging-new-message__list--skeleton"
        />
      ) : (
        <ul className="messaging-new-message__list">
          {!listUsers.length ? (
            <li className="messaging-new-message__empty">{emptyLabel}</li>
          ) : (
            listUsers.map((user) => (
              <UserRow
                key={user.userId || `${user.handle}-${user.displayName}`}
                user={user}
                onSelect={onSelectUser}
              />
            ))
          )}
        </ul>
      )}
    </div>
  );
}
